import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { BUILDER } from '@/constants/testIds';
import { AgentAvatar } from '@/components/AgentAvatar';
import { Wrench, Trash2, Play, GitBranch } from 'lucide-react';
import { toast } from 'sonner';

const MODELS = [
  { provider: 'openai', name: 'gpt-5', label: 'GPT-5 (OpenAI)' },
  { provider: 'anthropic', name: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6 (Anthropic)' },
  { provider: 'gemini', name: 'gemini-3-flash-preview', label: 'Gemini 3 Flash (Google)' },
];
const CATEGORIES = [
  'Sales & Lead-Gen', 'Marketing & Content', 'Paid Ads', 'Customer Support', 'Research & Strategy',
  'Operations & Automation', 'Finance', 'HR & Recruiting', 'Data & Analytics', 'Custom',
];

export default function Builder() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const forkId = params.get('fork');
  const [forkSource, setForkSource] = useState(null);

  const [form, setForm] = useState({
    name: '',
    role: '',
    description: '',
    category: 'Custom',
    system_prompt: '',
    model_provider: 'openai',
    model_name: 'gpt-5',
    forked_from: null,
  });

  // Fetch fork source if URL param present
  useEffect(() => {
    let active = true;
    const fetchFork = async () => {
      if (!forkId) return;
      try {
        const a = (await api.get(`/agents/${forkId}`)).data;
        if (!active) return;
        setForkSource(a);
        setForm({
          name: `Fork of ${a.name}`,
          role: a.role,
          description: a.description,
          category: a.category,
          system_prompt: a.system_prompt || '',
          model_provider: a.model_provider || 'openai',
          model_name: a.model_name || 'gpt-5',
          forked_from: a.agent_id,
        });
      } catch {
        toast.error('Could not load source agent for fork');
      }
    };
    fetchFork();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when the fork URL param changes
  }, [forkId]);

  const { data: myAgents } = useQuery({
    queryKey: ['my-agents'],
    queryFn: async () => (await api.get('/builder/agents')).data,
    enabled: !!user,
  });

  const createMut = useMutation({
    mutationFn: async () => (await api.post('/builder/agents', form)).data,
    onSuccess: (agent) => {
      toast.success(`Agent “${agent.name}” created`);
      qc.invalidateQueries({ queryKey: ['my-agents'] });
      navigate(`/chat?agent=${agent.agent_id}`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed to create agent'),
  });

  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/builder/agents/${id}`)).data,
    onSuccess: () => {
      toast.success('Agent deleted');
      qc.invalidateQueries({ queryKey: ['my-agents'] });
    },
  });

  const setModel = (val) => {
    const m = MODELS.find((x) => `${x.provider}/${x.name}` === val) || MODELS[0];
    setForm((f) => ({ ...f, model_provider: m.provider, model_name: m.name }));
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.role || !form.system_prompt) {
      toast.error('Name, role, and system prompt are required');
      return;
    }
    createMut.mutate();
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-20">
      <div className="flex items-end justify-between mb-8 gap-3 flex-wrap">
        <div>
          <span className="text-[10px] uppercase tracking-widest text-[#39FF14]">// builder</span>
          <h1 className="display text-3xl md:text-4xl font-semibold tracking-tight">Spin up a custom agent in 60 seconds.</h1>
          <p className="text-white/65 mt-2 max-w-2xl text-sm">Name a role. Drop in a system prompt. Pick a model. Ship.</p>
        </div>
      </div>

      {forkSource && (
        <div className="mb-5 hairline rounded-xl p-4 bg-[#39FF14]/5 flex items-center gap-3" data-testid="builder-fork-banner">
          <GitBranch className="h-5 w-5 text-[#39FF14]" />
          <div className="flex-1">
            <div className="text-xs uppercase tracking-widest text-[#39FF14]">// forking</div>
            <div className="text-sm">You're forking <span className="display font-semibold">{forkSource.name}</span> — edit anything below to make it yours.</div>
          </div>
          <Link to={`/agents/${forkSource.agent_id}`} className="btn-ghost rounded-md px-3 py-1.5 text-xs">View source</Link>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <form onSubmit={onSubmit} className="lg:col-span-2 card-base p-6 space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Agent name">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Pipeline Co-Pilot" data-testid={BUILDER.name} className="input" />
            </Field>
            <Field label="Role">
              <input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                placeholder="e.g. Outbound Strategist" data-testid={BUILDER.role} className="input" />
            </Field>
          </div>
          <Field label="Short description (1-2 sentences)">
            <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="What does this agent do?" className="input" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Category">
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input">
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Model">
              <select
                value={`${form.model_provider}/${form.model_name}`}
                onChange={(e) => setModel(e.target.value)}
                data-testid={BUILDER.modelSelect}
                className="input"
              >
                {MODELS.map((m) => (
                  <option key={`${m.provider}/${m.name}`} value={`${m.provider}/${m.name}`}>{m.label}</option>
                ))}
              </select>
            </Field>
          </div>
          <Field label="System prompt">
            <textarea
              rows={8}
              value={form.system_prompt}
              onChange={(e) => setForm({ ...form, system_prompt: e.target.value })}
              data-testid={BUILDER.prompt}
              placeholder="You are… Be precise. Output…"
              className="input min-h-[180px]"
            />
          </Field>
          <div className="flex items-center justify-end gap-2">
            <button type="submit" data-testid={BUILDER.submit} disabled={createMut.isPending}
              className="btn-primary inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-medium disabled:opacity-50">
              <Wrench className="h-4 w-4" /> {createMut.isPending ? 'Creating…' : 'Create & Chat'}
            </button>
          </div>
        </form>

        <div className="card-base p-6">
          <div className="text-[10px] uppercase tracking-widest text-[#FF6B1A] mb-3">// my agents</div>
          {!user ? (
            <p className="text-sm text-white/60">Sign in to save and reuse your custom agents.</p>
          ) : (myAgents?.items?.length || 0) === 0 ? (
            <p className="text-sm text-white/60">No custom agents yet. Build your first one on the left.</p>
          ) : (
            <ul className="space-y-3">
              {myAgents.items.map((a) => (
                <li key={a.agent_id} className="hairline rounded-xl p-3 flex items-center gap-3">
                  <AgentAvatar name={a.name} size={36} />
                  <div className="flex-1 min-w-0">
                    <div className="display text-sm truncate">{a.name}</div>
                    <div className="text-[11px] text-white/50 truncate">{a.role}</div>
                  </div>
                  <button onClick={() => navigate(`/chat?agent=${a.agent_id}`)} className="btn-ghost p-1.5 rounded text-[#39FF14]"><Play className="h-4 w-4" /></button>
                  <button onClick={() => delMut.mutate(a.agent_id)} className="btn-ghost p-1.5 rounded text-white/50 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs text-white/55 mb-1.5">{label}</div>
      {children}
    </label>
  );
}
